import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  CheckCircle,
  CreditCard,
  Mail,
  MapPin,
  ShieldCheck,
  User,
} from 'lucide-react'
import {
  Button,
  PasswordInput,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  ModernInput,
  ModernButton,
  ListPageSkeleton,
} from '../../components/ui'
import { DocumentInput, PhoneInput } from '../../components/form'
import { Plan } from '../../types'
import { apiService, ApiError, type RegisterResponse } from '../../services/api'
import { lookupZipCode } from '../../services/cepLookup'
import {
  BrazilAddressFields,
  BrazilAddressValue,
} from '../../components/address/BrazilAddressFields'
import { digitsOnly, formatCep, formatCnpj, formatCpf } from '../../utils/brFormat'
import { getLocalizedPublicPath } from '../../lib/publicLocaleRouting'

const emptyRegAddress = (): BrazilAddressValue => ({
  zipCode: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
})

type RegistrationStep = 'company' | 'user' | 'payment'

type RegistrationErrors = {
  document?: string
  legalName?: string
  tradeName?: string
  email?: string
  phone?: string
  address?: string
  name?: string
  userEmail?: string
  password?: string
  confirmPassword?: string
  submit?: string
}

type RegistrationAddressErrors = Partial<Record<keyof BrazilAddressValue, boolean>>

type DocumentValidationState = {
  isValidating: boolean
  isValid: boolean | null
  message: string | null
}

type StepMeta = {
  step: RegistrationStep
  number: number
  title: string
  description: string
}

const REGISTRATION_STEPS: RegistrationStep[] = ['company', 'user', 'payment']

const formatPlanPriceLabel = (
  selectedPlan: Plan,
  currencyFormatter: Intl.NumberFormat,
  freeLabel: string,
): string => {
  if (selectedPlan.priceMonthly === 0) return freeLabel
  return currencyFormatter.format(selectedPlan.priceMonthly)
}

export const DealershipRegistrationPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
  const [currentStep, setCurrentStep] = useState<RegistrationStep>('company')
  const [isLoading, setIsLoading] = useState(false)

  const [companyData, setCompanyData] = useState({
    document: '',
    documentType: 'pj' as 'pf' | 'pj',
    legalName: '',
    tradeName: '',
    email: '',
    phone: '',
    country: 'BR',
  })

  const [regAddress, setRegAddress] = useState<BrazilAddressValue>(emptyRegAddress)
  const [regZipLoading, setRegZipLoading] = useState(false)
  const [regZipError, setRegZipError] = useState<string | null>(null)
  const [regAddrLockedFromCep, setRegAddrLockedFromCep] = useState(false)
  const [regAddressErrors, setRegAddressErrors] = useState<RegistrationAddressErrors>({})
  const lastSuccessfulRegCepRef = useRef('')

  const [userData, setUserData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  })

  const [errors, setErrors] = useState<RegistrationErrors>({})
  const [documentValidation, setDocumentValidation] = useState<DocumentValidationState>({
    isValidating: false,
    isValid: null,
    message: null,
  })

  const locale = useMemo(() => {
    if (i18n.language.startsWith('en')) return 'en-US'
    if (i18n.language.startsWith('es')) return 'es-ES'
    return 'pt-BR'
  }, [i18n.language])

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL' }),
    [locale],
  )

  const companyFieldsVisible = documentValidation.isValid === true
  const isCompanyPf = companyData.documentType === 'pf'

  useEffect(() => {
    const savedPlan = localStorage.getItem('selectedPlan')
    if (savedPlan) {
      try {
        setSelectedPlan(JSON.parse(savedPlan) as Plan)
      } catch {
        navigate(getLocalizedPublicPath(location.pathname, '/plans', i18n.resolvedLanguage || i18n.language))
      }
    } else {
      navigate(getLocalizedPublicPath(location.pathname, '/plans', i18n.resolvedLanguage || i18n.language))
    }
  }, [location.pathname, navigate, i18n.language, i18n.resolvedLanguage])

  const clearFieldError = useCallback((field: keyof RegistrationErrors) => {
    setErrors(prev => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }, [])

  const clearAddressFieldError = useCallback((field: keyof RegistrationAddressErrors) => {
    setRegAddressErrors(prev => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }, [])

  const validateDocumentWithBackend = useCallback(
    async (document: string, documentType: 'pf' | 'pj') => {
      const numbers = digitsOnly(document)
      const expectedDigits = documentType === 'pf' ? 11 : 14
      const validatingMessage =
        documentType === 'pf'
          ? t('settings.dealership.registration.validation.cpfValidating')
          : t('settings.dealership.registration.validation.cnpjValidating')
      const lengthMessage =
        documentType === 'pf'
          ? t('settings.dealership.registration.validation.cpfLength')
          : t('settings.dealership.registration.validation.cnpjLength')
      const invalidMessage =
        documentType === 'pf'
          ? t('settings.dealership.registration.validation.cpfInvalid')
          : t('settings.dealership.registration.validation.cnpjInvalid')
      const validationErrorMessage =
        documentType === 'pf'
          ? t('settings.dealership.registration.validation.cpfValidationError')
          : t('settings.dealership.registration.validation.cnpjValidationError')

      if (numbers.length !== expectedDigits) {
        setDocumentValidation({
          isValidating: false,
          isValid: false,
          message: lengthMessage,
        })
        setErrors(prev => ({ ...prev, document: lengthMessage }))
        return false
      }

      setDocumentValidation({
        isValidating: true,
        isValid: null,
        message: validatingMessage,
      })

      try {
        const result = await apiService.validateDocument(numbers)
        const isExpectedDocument = result.valid && result.type === (documentType === 'pf' ? 'cpf' : 'cnpj')
        const message = isExpectedDocument ? result.message : result.message || invalidMessage

        setDocumentValidation({
          isValidating: false,
          isValid: isExpectedDocument,
          message,
        })

        if (!isExpectedDocument) {
          setErrors(prev => ({ ...prev, document: message }))
        } else {
          clearFieldError('document')
        }

        return isExpectedDocument
      } catch {
        setDocumentValidation({
          isValidating: false,
          isValid: false,
          message: validationErrorMessage,
        })
        setErrors(prev => ({ ...prev, document: validationErrorMessage }))
        return false
      }
    },
    [clearFieldError, t],
  )

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^\(\d{2}\)\s\d{4,5}-\d{4}$/
    return phoneRegex.test(phone)
  }

  const validateCompanyForm = (): boolean => {
    const newErrors: RegistrationErrors = {}
    const documentDigits = digitsOnly(companyData.document)
    const expectedDigits = companyData.documentType === 'pf' ? 11 : 14
    const requiredMessage =
      companyData.documentType === 'pf'
        ? t('settings.dealership.registration.validation.cpfRequired')
        : t('settings.dealership.registration.validation.cnpjRequired')
    const lengthMessage =
      companyData.documentType === 'pf'
        ? t('settings.dealership.registration.validation.cpfLength')
        : t('settings.dealership.registration.validation.cnpjLength')
    const invalidMessage =
      companyData.documentType === 'pf'
        ? t('settings.dealership.registration.validation.cpfInvalid')
        : t('settings.dealership.registration.validation.cnpjInvalid')

    if (!companyData.document) {
      newErrors.document = requiredMessage
    } else if (documentDigits.length !== expectedDigits) {
      newErrors.document = lengthMessage
    } else if (documentValidation.isValid === false) {
      newErrors.document = documentValidation.message || invalidMessage
    }

    if (!companyData.legalName) {
      newErrors.legalName = isCompanyPf
        ? t('settings.dealership.registration.validation.cpfNameRequired')
        : t('settings.dealership.registration.validation.legalNameRequired')
    }

    if (!companyData.email) {
      newErrors.email = t('settings.dealership.registration.validation.companyEmailRequired')
    } else if (!validateEmail(companyData.email)) {
      newErrors.email = t('settings.dealership.registration.validation.invalidEmail')
    }

    if (!companyData.phone) {
      newErrors.phone = t('settings.dealership.registration.validation.phoneRequired')
    } else if (!validatePhone(companyData.phone)) {
      newErrors.phone = t('settings.dealership.registration.validation.invalidPhone')
    }

    const nextAddressErrors: RegistrationAddressErrors = {}
    const zipDigits = digitsOnly(regAddress.zipCode)
    if (zipDigits.length !== 8) nextAddressErrors.zipCode = true
    if (!regAddress.street.trim()) nextAddressErrors.street = true
    if (!regAddress.number.trim()) nextAddressErrors.number = true
    if (!regAddress.neighborhood.trim()) nextAddressErrors.neighborhood = true
    if (!regAddress.city.trim()) nextAddressErrors.city = true
    if (regAddress.state.trim().length !== 2) nextAddressErrors.state = true

    if (Object.keys(nextAddressErrors).length > 0) {
      newErrors.address = t('settings.dealership.registration.validation.addressRequired')
      setRegAddressErrors(nextAddressErrors)
    } else {
      setRegAddressErrors({})
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateUserForm = (): boolean => {
    const newErrors: RegistrationErrors = {}

    if (!userData.name) {
      newErrors.name = t('settings.dealership.registration.validation.userNameRequired')
    }

    if (!userData.email) {
      newErrors.userEmail = t('settings.dealership.registration.validation.userEmailRequired')
    } else if (!validateEmail(userData.email)) {
      newErrors.userEmail = t('settings.dealership.registration.validation.invalidEmail')
    }

    if (!userData.password) {
      newErrors.password = t('settings.dealership.registration.validation.passwordRequired')
    } else if (userData.password.length < 6) {
      newErrors.password = t('settings.dealership.registration.validation.passwordLength')
    }

    if (!userData.confirmPassword) {
      newErrors.confirmPassword = t('settings.dealership.registration.validation.confirmPasswordRequired')
    } else if (userData.password !== userData.confirmPassword) {
      newErrors.confirmPassword = t('settings.dealership.registration.validation.passwordMismatch')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = async (): Promise<void> => {
    if (currentStep === 'company') {
      if (!validateCompanyForm()) return

      try {
        setIsLoading(true)
        const isCompanyDocument =
          documentValidation.isValid === true
            ? true
            : await validateDocumentWithBackend(companyData.document, companyData.documentType)

        if (!isCompanyDocument) {
          return
        }

        setCurrentStep('user')
      } catch (error: unknown) {
        const message =
          error instanceof Error && error.message.includes('Too Many Requests')
            ? t('settings.dealership.registration.validation.tooManyRequests')
            : companyData.documentType === 'pf'
              ? t('settings.dealership.registration.validation.cpfValidationError')
              : t('settings.dealership.registration.validation.cnpjValidationError')

        setErrors({ document: message })
      } finally {
        setIsLoading(false)
      }
      return
    }

    if (currentStep === 'user' && validateUserForm()) {
      setCurrentStep('payment')
    }
  }

  const handleBack = (): void => {
    if (currentStep === 'user') {
      setCurrentStep('company')
    } else if (currentStep === 'payment') {
      setCurrentStep('user')
    } else {
      navigate(getLocalizedPublicPath(location.pathname, '/plans', i18n.resolvedLanguage || i18n.language))
    }
  }

  const applyRegAddressChange = useCallback((next: BrazilAddressValue) => {
    if (digitsOnly(next.zipCode) !== lastSuccessfulRegCepRef.current) {
      setRegAddrLockedFromCep(false)
    }
    setRegAddress(next)
    setRegAddressErrors(prev => {
      const nextErrors = { ...prev }
      if (digitsOnly(next.zipCode).length === 8) delete nextErrors.zipCode
      if (next.street.trim()) delete nextErrors.street
      if (next.number.trim()) delete nextErrors.number
      if (next.neighborhood.trim()) delete nextErrors.neighborhood
      if (next.city.trim()) delete nextErrors.city
      if (next.state.trim().length === 2) delete nextErrors.state
      return nextErrors
    })
  }, [])

  const runRegCepLookup = useCallback(
    async (strictIncomplete: boolean) => {
      const normalized = digitsOnly(regAddress.zipCode)
      if (normalized.length !== 8) {
        if (strictIncomplete) {
          setRegZipError(t('customers.address.zipInvalid'))
          setRegAddressErrors(prev => ({ ...prev, zipCode: true }))
        }
        return
      }

      setRegZipError(null)
      setRegAddressErrors(prev => {
        if (!prev.zipCode) return prev
        const next = { ...prev }
        delete next.zipCode
        return next
      })

      try {
        setRegZipLoading(true)
        const res = await lookupZipCode(normalized, { publicOnly: true })
        lastSuccessfulRegCepRef.current = normalized
        setRegAddrLockedFromCep(true)
        setRegAddress(prev => ({
          ...prev,
          zipCode: formatCep(res.zipCode || normalized),
          street: (res.street && res.street.trim()) || prev.street,
          neighborhood: (res.neighborhood && res.neighborhood.trim()) || prev.neighborhood,
          city: (res.city && res.city.trim()) || prev.city,
          state:
            (res.state && res.state.trim().toUpperCase().slice(0, 2)) ||
            prev.state.toUpperCase().slice(0, 2),
          complement:
            prev.complement?.trim() || (res.complement && res.complement.trim()) || '',
        }))
      } catch (error: unknown) {
        lastSuccessfulRegCepRef.current = ''
        setRegAddrLockedFromCep(false)
        const msg = error instanceof Error ? error.message : t('customers.address.zipLookupError')
        setRegZipError(msg)
        setRegAddressErrors(prev => ({ ...prev, zipCode: true }))
      } finally {
        setRegZipLoading(false)
      }
    },
    [regAddress.zipCode, t],
  )

  useEffect(() => {
    const normalized = digitsOnly(regAddress.zipCode)
    if (normalized.length === 8 && normalized !== lastSuccessfulRegCepRef.current) {
      const timer = setTimeout(() => {
        void runRegCepLookup(false)
      }, 500)

      return () => clearTimeout(timer)
    }
  }, [regAddress.zipCode, runRegCepLookup])

  const handleSubmit = async (): Promise<void> => {
    if (!selectedPlan) return

    setIsLoading(true)

    try {
      const registerData = {
        name: userData.name,
        email: userData.email,
        password: userData.password,
        planId: selectedPlan.id,
        country: companyData.country,
        cpf: companyData.documentType === 'pf' ? companyData.document : undefined,
        cnpj: companyData.documentType === 'pj' ? companyData.document : undefined,
        taxId: companyData.document,
        dealershipName: companyData.legalName,
        tradeName: companyData.tradeName || companyData.legalName,
        dealershipEmail: companyData.email,
        phone: companyData.phone,
        address: {
          zipCode: digitsOnly(regAddress.zipCode),
          street: regAddress.street.trim(),
          number: regAddress.number.trim(),
          complement: regAddress.complement.trim() || undefined,
          neighborhood: regAddress.neighborhood.trim(),
          city: regAddress.city.trim(),
          state: regAddress.state.trim().toUpperCase(),
        },
      }

      const response = await apiService.register(registerData)
      localStorage.removeItem('selectedPlan')

      const responseData: RegisterResponse = response.data ?? response as unknown as RegisterResponse

      if ('accessToken' in responseData) {
        localStorage.setItem('accessToken', responseData.accessToken)
        localStorage.setItem('refreshToken', responseData.refreshToken)
        localStorage.setItem('user', JSON.stringify(responseData.user))
        localStorage.setItem('dealership', JSON.stringify(responseData.dealership))
        navigate('/')
      } else {
        navigate(getLocalizedPublicPath(location.pathname, '/verify-email', i18n.resolvedLanguage || i18n.language), {
          state: {
            email: userData.email,
          },
        })
      }
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        setErrors({
          submit: error.message || t('settings.dealership.registration.validation.submitError'),
        })
      } else {
        setErrors({
          submit: t('settings.dealership.registration.validation.connectionError'),
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const currentStepIndex = REGISTRATION_STEPS.indexOf(currentStep)
  const currentStepNumber = currentStepIndex + 1
  const stepProgress = ((currentStepNumber - 1) / (REGISTRATION_STEPS.length - 1)) * 100
  const isPaymentStep = currentStep === 'payment'
  const companyDocument =
    companyData.documentType === 'pf' ? formatCpf(companyData.document) : formatCnpj(companyData.document)
  const companyDocumentLabel =
    companyData.documentType === 'pf'
      ? t('settings.dealership.registration.company.cpf')
      : t('settings.dealership.registration.company.cnpj')

  const stepMeta: StepMeta[] = useMemo(
    () => [
      {
        step: 'company',
        number: 1,
        title: t('settings.dealership.registration.stepTitles.company'),
        description: t('settings.dealership.registration.stepDescriptions.company'),
      },
      {
        step: 'user',
        number: 2,
        title: t('settings.dealership.registration.stepTitles.user'),
        description: t('settings.dealership.registration.stepDescriptions.user'),
      },
      {
        step: 'payment',
        number: 3,
        title: t('settings.dealership.registration.stepTitles.payment'),
        description: t('settings.dealership.registration.stepDescriptions.payment'),
      },
    ],
    [t],
  )

  const summaryItems = useMemo(
    () => [
      {
        label: t('settings.dealership.registration.sidebar.planSummary'),
        value: selectedPlan?.name || '',
      },
      {
        label: t('settings.dealership.registration.sidebar.planUsers'),
        value:
          selectedPlan?.userLimit === -1
            ? t('settings.dealership.registration.sidebar.usersUnlimited')
            : t('settings.dealership.registration.sidebar.usersCount', { count: selectedPlan?.userLimit || 0 }),
      },
      {
        label: t('settings.dealership.registration.sidebar.planVehicles'),
        value:
          selectedPlan?.vehicleLimit === -1
            ? t('settings.dealership.registration.payment.vehiclesUnlimited')
            : t('settings.dealership.registration.payment.vehicles', {
                count: selectedPlan?.vehicleLimit || 0,
              }),
      },
    ],
    [selectedPlan, t],
  )

  if (!selectedPlan) {
    return <ListPageSkeleton variant="cards" rows={4} showFilters={false} />
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <div className="grid gap-8 xl:grid-cols-[minmax(0,1.45fr)_380px] xl:items-start">
          <div className="space-y-6">
            <Card className="border-border/70 bg-card/95 shadow-[0_18px_54px_-32px_rgba(15,23,42,0.35)] backdrop-blur">
              <CardHeader className="space-y-4 border-b border-border/50 pb-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="space-y-2">
                    <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-muted/40 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                      <Building2 className="h-4 w-4 text-primary" />
                      {t('settings.dealership.registration.form.eyebrow')}
                    </div>
                    <CardTitle className="text-2xl sm:text-3xl">
                      {t('settings.dealership.registration.stepTitles.' + currentStep)}
                    </CardTitle>
                    <p className="max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
                      {t('settings.dealership.registration.stepDescriptions.' + currentStep)}
                    </p>
                  </div>

                  <div className="hidden min-w-[280px] flex-1 xl:block">
                    <div className="rounded-2xl border border-border/70 bg-muted/30 p-4">
                      <div className="mb-3 flex items-center justify-between text-sm text-muted-foreground">
                        <span>{t('settings.dealership.registration.sidebar.progressLabel')}</span>
                        <span>
                          {t('settings.dealership.registration.sidebar.progressValue', {
                            current: currentStepNumber,
                            total: REGISTRATION_STEPS.length,
                          })}
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-primary via-primary/80 to-emerald-500 transition-all duration-300"
                          style={{ width: `${stepProgress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  {stepMeta.map(step => {
                    const isActive = currentStep === step.step
                    const isDone = currentStepIndex > step.number - 1

                    return (
                      <div
                        key={step.step}
                        className={`rounded-2xl border p-3 transition-all duration-200 ${
                          isActive
                            ? 'border-primary/30 bg-primary/5 shadow-sm'
                            : isDone
                              ? 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-900 dark:bg-emerald-950/30'
                              : 'border-border/70 bg-muted/20'
                        }`}
                      >
                        <div className="mb-3 flex items-center justify-between">
                          <div
                            className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                              isActive
                                ? 'bg-primary text-primary-foreground'
                                : isDone
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {isDone ? <CheckCircle className="h-4 w-4" /> : step.number}
                          </div>
                          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                            {t('settings.dealership.registration.sidebar.stepPrefix', { count: step.number })}
                          </span>
                        </div>
                        <p className="font-semibold text-foreground">{step.title}</p>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">{step.description}</p>
                      </div>
                    )
                  })}
                </div>
              </CardHeader>

              <CardContent className="space-y-6 p-6 sm:p-8">
                {currentStep === 'company' && (
                  <div className="space-y-5">
                    <DocumentInput
                      label={t('settings.dealership.registration.company.document')}
                      required
                      value={companyData.document}
                      personType={companyData.documentType}
                      onPersonTypeChange={(type: 'pf' | 'pj') => {
                        setCompanyData(prev => ({ ...prev, documentType: type }))
                        clearFieldError('document')
                        setDocumentValidation({ isValidating: false, isValid: null, message: null })
                      }}
                      onChange={(value: string, type: 'cpf' | 'cnpj') => {
                        setCompanyData(prev => ({
                          ...prev,
                          document: value,
                          documentType: type === 'cpf' ? 'pf' : 'pj',
                        }))
                        clearFieldError('document')
                        setDocumentValidation({ isValidating: false, isValid: null, message: null })
                      }}
                      onBlur={() => {
                        if (companyData.document) {
                          void validateDocumentWithBackend(companyData.document, companyData.documentType)
                        }
                      }}
                      error={errors.document}
                      validationMessage={documentValidation.message}
                      isValidating={documentValidation.isValidating}
                    />

                    {companyFieldsVisible && (
                      <div className="space-y-5">
                        <div className="grid gap-5 lg:grid-cols-2">
                          <ModernInput
                            label={
                              isCompanyPf
                                ? t('settings.dealership.registration.company.cpfName')
                                : t('settings.dealership.registration.company.legalName')
                            }
                            value={companyData.legalName}
                            onChange={e => {
                              setCompanyData(prev => ({ ...prev, legalName: e.target.value }))
                              clearFieldError('legalName')
                            }}
                            placeholder={
                              isCompanyPf
                                ? t('settings.dealership.registration.company.cpfNamePlaceholder')
                                : t('settings.dealership.registration.company.legalNamePlaceholder')
                            }
                            error={errors.legalName}
                            required
                            leftIcon={<Building2 className="h-5 w-5" />}
                          />

                          {!isCompanyPf && (
                            <ModernInput
                              label={t('settings.dealership.registration.company.tradeName')}
                              value={companyData.tradeName}
                              onChange={e => {
                                setCompanyData(prev => ({ ...prev, tradeName: e.target.value }))
                                clearFieldError('tradeName')
                              }}
                              placeholder={t('settings.dealership.registration.company.tradeNamePlaceholder')}
                              error={errors.tradeName}
                            />
                          )}
                        </div>

                        <div className="grid gap-5 lg:grid-cols-2">
                          <ModernInput
                            label={t('settings.dealership.registration.company.email')}
                            required
                            type="email"
                            value={companyData.email}
                            onChange={e => {
                              setCompanyData(prev => ({ ...prev, email: e.target.value }))
                              clearFieldError('email')
                            }}
                            placeholder={t('settings.dealership.registration.company.emailPlaceholder')}
                            error={errors.email}
                            leftIcon={<Mail className="h-5 w-5 text-muted-foreground" />}
                          />
                          <PhoneInput
                            label={t('settings.dealership.registration.company.phone')}
                            required
                            value={companyData.phone}
                            onChange={value => {
                              setCompanyData(prev => ({ ...prev, phone: value }))
                              clearFieldError('phone')
                            }}
                            placeholder={t('settings.dealership.registration.company.phonePlaceholder')}
                            error={errors.phone}
                          />
                        </div>

                        <div className="rounded-2xl border border-border/70 bg-muted/20 p-4 shadow-sm">
                          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                            <MapPin className="h-4 w-4 text-primary" />
                            {t('settings.dealership.registration.company.address')}
                          </div>
                          <BrazilAddressFields
                            value={regAddress}
                            onChange={applyRegAddressChange}
                            onZipBlur={() => void runRegCepLookup(false)}
                            onZipSearchPress={() => void runRegCepLookup(true)}
                            isLoadingZip={regZipLoading}
                            zipError={regZipError}
                            fieldErrors={regAddressErrors}
                            lockDerivedAddressFields={regAddrLockedFromCep}
                            i18nPrefix="customers.address"
                          />
                          {errors.address && (
                            <p className="mt-3 text-sm text-red-600 dark:text-red-400">{errors.address}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {currentStep === 'user' && (
                  <div className="space-y-5">
                    <div className="rounded-2xl border border-border/70 bg-muted/20 p-4">
                      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                        <ShieldCheck className="h-4 w-4 text-primary" />
                        {t('settings.dealership.registration.user.sectionTitle')}
                      </div>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        {t('settings.dealership.registration.user.sectionSubtitle')}
                      </p>
                    </div>

                    <ModernInput
                      label={t('settings.dealership.registration.user.name')}
                      required
                      value={userData.name}
                      onChange={e => {
                        setUserData(prev => ({ ...prev, name: e.target.value }))
                        clearFieldError('name')
                      }}
                      placeholder={t('settings.dealership.registration.user.namePlaceholder')}
                      error={errors.name}
                      leftIcon={<User className="h-5 w-5" />}
                    />

                    <ModernInput
                      label={t('settings.dealership.registration.user.email')}
                      required
                      type="email"
                      value={userData.email}
                      onChange={e => {
                        setUserData(prev => ({ ...prev, email: e.target.value }))
                        clearFieldError('userEmail')
                      }}
                      placeholder={t('settings.dealership.registration.user.emailPlaceholder')}
                      error={errors.userEmail}
                      leftIcon={<Mail className="h-5 w-5" />}
                    />

                    <div className="grid gap-5 lg:grid-cols-2">
                      <PasswordInput
                        label={t('settings.dealership.registration.user.password')}
                        required
                        value={userData.password}
                        onChange={e => {
                          setUserData(prev => ({ ...prev, password: e.target.value }))
                          clearFieldError('password')
                        }}
                        placeholder={t('settings.dealership.registration.user.passwordPlaceholder')}
                        error={errors.password}
                      />
                      <PasswordInput
                        label={t('settings.dealership.registration.user.confirmPassword')}
                        required
                        value={userData.confirmPassword}
                        onChange={e => {
                          setUserData(prev => ({ ...prev, confirmPassword: e.target.value }))
                          clearFieldError('confirmPassword')
                        }}
                        placeholder={t('settings.dealership.registration.user.confirmPasswordPlaceholder')}
                        error={errors.confirmPassword}
                      />
                    </div>
                  </div>
                )}

                {isPaymentStep && (
                  <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
                    <div className="space-y-4">
                      <div className="rounded-2xl border border-border/70 bg-muted/20 p-4 shadow-sm">
                        <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          {t('settings.dealership.registration.payment.selectedPlan')}
                        </h3>
                        <div className="mt-3 flex items-start justify-between gap-4">
                          <div>
                            <p className="text-lg font-semibold text-foreground">{selectedPlan.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {selectedPlan.vehicleLimit === -1
                                ? t('settings.dealership.registration.payment.vehiclesUnlimited')
                                : t('settings.dealership.registration.payment.vehicles', {
                                    count: selectedPlan.vehicleLimit,
                                  })}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-semibold text-foreground">
                              {formatPlanPriceLabel(
                                selectedPlan,
                                currencyFormatter,
                                t('settings.dealership.registration.payment.free'),
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {t('settings.dealership.registration.payment.perMonth')}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-border/70 bg-muted/20 p-4 shadow-sm">
                        <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          {t('settings.dealership.registration.payment.companySummary')}
                        </h3>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                              {t('settings.dealership.registration.company.legalName')}
                            </p>
                            <p className="mt-1 font-medium text-foreground">{companyData.legalName}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                              {companyDocumentLabel}
                            </p>
                            <p className="mt-1 font-medium text-foreground">{companyDocument}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                              {t('settings.dealership.registration.company.tradeName')}
                            </p>
                            <p className="mt-1 font-medium text-foreground">
                              {companyData.tradeName || t('settings.dealership.registration.common.notProvided')}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                              {t('settings.dealership.registration.company.email')}
                            </p>
                            <p className="mt-1 font-medium text-foreground">{companyData.email}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                              {t('settings.dealership.registration.company.phone')}
                            </p>
                            <p className="mt-1 font-medium text-foreground">{companyData.phone}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                              {t('settings.dealership.registration.user.name')}
                            </p>
                            <p className="mt-1 font-medium text-foreground">{userData.name}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 rounded-2xl border border-border/70 bg-background p-4 shadow-sm">
                      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                        <ShieldCheck className="h-4 w-4 text-primary" />
                        {t('settings.dealership.registration.payment.termsTitle')}
                      </div>
                      <p className="text-sm leading-6 text-muted-foreground">
                        {t('settings.dealership.registration.payment.termsPrefix')}{' '}
                        <a href="#" className="font-medium text-primary underline-offset-4 hover:underline">
                          {t('settings.dealership.registration.payment.termsLink')}
                        </a>{' '}
                        {t('settings.dealership.registration.payment.and')}{' '}
                        <a href="#" className="font-medium text-primary underline-offset-4 hover:underline">
                          {t('settings.dealership.registration.payment.privacyLink')}
                        </a>
                        .
                      </p>
                    </div>
                  </div>
                )}

                {errors.submit && (
                  <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
                      <p className="text-sm leading-6 text-destructive">{errors.submit}</p>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-3 border-t border-border/50 pt-6 sm:flex-row sm:items-center sm:justify-between">
                  <Button variant="outline" onClick={handleBack} className="justify-center sm:justify-start">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t('settings.dealership.registration.actions.back')}
                  </Button>

                  {isPaymentStep ? (
                    <ModernButton onClick={handleSubmit} loading={isLoading} className="justify-center">
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t('settings.dealership.registration.actions.finish')}
                    </ModernButton>
                  ) : (
                    <Button onClick={handleNext} disabled={isLoading} className="justify-center">
                      {isLoading
                        ? t('settings.dealership.registration.actions.validating')
                        : t('settings.dealership.registration.actions.continue')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="pb-2 text-center">
              <p className="text-sm text-muted-foreground">
                {t('settings.dealership.registration.login.prompt')}{' '}
                <button
                  onClick={() =>
                    navigate(
                      getLocalizedPublicPath(
                        location.pathname,
                        '/login',
                        i18n.resolvedLanguage || i18n.language,
                      ),
                    )
                  }
                  className="font-medium text-primary underline-offset-4 transition-colors hover:underline"
                >
                  {t('settings.dealership.registration.login.link')}
                </button>
              </p>
            </div>
          </div>

          <aside className="space-y-6 lg:sticky lg:top-8 lg:self-start">
            <Card className="border-border/70 bg-card/95 shadow-[0_18px_54px_-34px_rgba(15,23,42,0.45)] backdrop-blur">
              <CardHeader className="space-y-4 pb-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                      {t('settings.dealership.registration.sidebar.quickSummary')}
                    </p>
                    <CardTitle className="mt-2 text-xl">
                      {t('settings.dealership.registration.sidebar.title')}
                    </CardTitle>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-muted/40 p-3 text-primary shadow-sm">
                    <CreditCard className="h-5 w-5" />
                  </div>
                </div>

                <div className="rounded-2xl border border-border/70 bg-muted/30 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">{selectedPlan.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatPlanPriceLabel(
                          selectedPlan,
                          currencyFormatter,
                          t('settings.dealership.registration.payment.free'),
                        )}
                        <span className="ml-1">{t('settings.dealership.registration.payment.perMonth')}</span>
                      </p>
                    </div>
                    <div className="rounded-full bg-background px-3 py-1 text-xs font-semibold text-muted-foreground shadow-sm">
                      {t('settings.dealership.registration.sidebar.planActive')}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                <div className="rounded-2xl border border-border/70 bg-muted/20 p-4">
                  <div className="mb-3 flex items-center justify-between text-sm text-muted-foreground">
                    <span>{t('settings.dealership.registration.sidebar.progressLabel')}</span>
                    <span>
                      {t('settings.dealership.registration.sidebar.progressValue', {
                        current: currentStepNumber,
                        total: REGISTRATION_STEPS.length,
                      })}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary via-primary/80 to-emerald-500 transition-all duration-300"
                      style={{ width: `${stepProgress}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  {summaryItems.map(item => (
                    <div key={item.label} className="flex items-start justify-between gap-4 rounded-2xl border border-border/70 bg-background p-4">
                      <span className="text-sm text-muted-foreground">{item.label}</span>
                      <span className="text-sm font-medium text-foreground text-right">{item.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  )
}
